import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography, Divider, Button, Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import GetAppIcon from '@mui/icons-material/GetApp';
import { CollectorScope, Collector, CollectorType } from 'graphql/types';
import { CollectorInstallInfo } from 'modules/Collectors/CollectorsListPage';
import { useHandleDisconnect } from '../hooks';

export type CollectorInfoProps = {
  onClose: () => void;
  collector: Collector;
  installDate?: string;
  collectorInstallInfo: CollectorInstallInfo;
};

const useStyles = makeStyles((theme) => ({
  collectorIcon: {
    alignSelf: 'center',
    display: 'flex',
    height: '210px',
    width: '210px',
    borderRadius: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  leftContainer: {
    width: '210px',
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(4)
  },
  text: {
    display: 'flex'
  },
  divider: {
    display: 'flex',
    margin: theme.spacing(2, -4)
  },
  installedBy: {},
  installedDate: {
    marginTop: theme.spacing(2)
  },
  connected: {
    display: 'flex',
    alignSelf: 'center',
    marginTop: theme.spacing(-1.25),
    ...theme.typography.caption,
    color: theme.palette.success.light,
    backgroundColor: theme.palette.success.dark,
    width: 'fit-content',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0, 0.8)
  },
  publishedBy: {
    marginTop: theme.spacing(2)
  },
  disconnect: {
    borderRadius: theme.spacing(2.5),
    marginTop: theme.spacing(2)
  },
  container: {
    display: 'flex',
    flexDirection: 'column'
  },
  desktopLink: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    background: theme.palette.primary.main,
    borderRadius: theme.spacing(2.5),
    color: theme.palette.common.white,
    padding: theme.spacing(1),
    textTransform: 'uppercase'
  },
  downloadIcon: {
    marginLeft: theme.spacing(1)
  }
}));

export const CollectorInfo: React.FC<CollectorInfoProps> = ({
  onClose,
  collector,
  installDate,
  collectorInstallInfo
}: CollectorInfoProps) => {
  const classes = useStyles();

  const onDisconnect = useHandleDisconnect({
    onClose,
    scope: CollectorScope.Tenant
  });
  const isDesktop = useMemo(() => collector.type === CollectorType.Desktop, [
    collector.type
  ]);

  const desktopLink = useMemo(
    () =>
      collector.installURL ||
      'https://drive.google.com/drive/u/0/folders/1mhE3Ad6vWvplJfoNAKDQWQ_0Z37tgziD',
    [collector.installURL]
  );

  return (
    <div
      className={classes.leftContainer}
      data-testid={`${collector.name}-info-container`}
    >
      <div className={classes.container}>
        <img className={classes.collectorIcon} src={collector.image.src} />
        <Typography
          className={classes.connected}
          variant="caption"
          data-testid={'connected-button-' + collector.name}
        >
          <FormattedMessage id="collectors.connected" />
        </Typography>
        {isDesktop ? (
          <Link
            href={desktopLink}
            target="_blank"
            color="primary"
            underline="none"
            className={classes.desktopLink}
          >
            <FormattedMessage id="collectorSetting.downLoad" />
            <GetAppIcon className={classes.downloadIcon} />
          </Link>
        ) : (
          <Button
            data-testid={
              'uninstall-button-' + collectorInstallInfo?.collectorItem?.name
            }
            onClick={onDisconnect(collector)}
            color={'primary'}
            variant={'contained'}
            className={classes.disconnect}
          >
            <FormattedMessage id="collectorSetting.uninstall" />
          </Button>
        )}

        <div className={classNames(classes.text, classes.publishedBy)}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="collectors.by" />
          </Typography>
          &nbsp;
          <Typography
            variant="body2"
            color="textPrimary"
            data-testid={'published-by'}
          >
            {collector.publishedBy}
          </Typography>
        </div>

        <div className={classes.text}>
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="collectors.category" />
          </Typography>
          &nbsp;
          <Typography
            variant="body2"
            color="textPrimary"
            data-testid={'collector-category'}
          >
            {collector.category}
          </Typography>
        </div>

        <Divider className={classes.divider} orientation="horizontal" />

        <Typography
          className={classNames(classes.text, classes.installedBy)}
          variant="body2"
          color="textSecondary"
        >
          <FormattedMessage id={'installedCollectors.installedBy'} />
        </Typography>

        <Typography
          className={classes.text}
          variant="body2"
          color="textPrimary"
        >
          <FormattedMessage id={'installedBy.admin'} />
        </Typography>

        <Typography
          className={classes.text}
          variant="body2"
          color="textPrimary"
        >
          <FormattedMessage id={'collectors.asCompanyLevel'} />
        </Typography>

        <Typography
          className={classes.installedDate}
          variant="caption"
          color="textSecondary"
        >
          {installDate}
        </Typography>
      </div>
    </div>
  );
};
